import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuid } from "uuid";
import { SelectPicker } from "rsuite";
import cn from "classnames";

import constants from "utils/constants";
import { type IConvertedItem } from "utils/url-helper";

import useUrlToPathConversion from "hooks/useUrlToPathConvertation";

import { type AppDispatch, type RootState } from "store/store";
import { type ITenant } from "store/environments/tenants/tenants-types";
import { type IEnvironment } from "store/environments/environment-types";
import { type ICustomer } from "store/customers/customers-types";
import { type IPartner } from "store/partners/partners-types";
import {
    getActiveEnvironmentId,
    getEnvironments,
} from "store/environments/environment-selectors";
import {
    getIsActiveTenantSet,
    getTenants,
} from "store/environments/tenants/tenants-selectors";
import { getCustomers } from "store/customers/customers-selectors";
import { getPartners } from "store/partners/partners-selectors";
import {
    setIsUnsavedChangeAvailable,
    setModalConfirmCallback,
} from "store/common/unsaved-data/unsaved-data-actions";
import { setRefreshActivated } from "store/common/page-refresh/page-refresh-actions";

import css from "./BreadCrumb.module.css";

interface Props {
    item: IConvertedItem;
    index: number;
}

const BreadCrumbSwitch: React.FC<Props> = ({ item, index }: Props) => {
    const itemPath = useUrlToPathConversion(item.url);
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const environments = useSelector((state: RootState) =>
        getEnvironments(state)
    );
    const environmentId = useSelector((state: RootState) =>
        getActiveEnvironmentId(state)
    );
    const tenants = useSelector((state: RootState) => getTenants(state));
    const customers = useSelector((state: RootState) => getCustomers(state));
    const partners = useSelector((state: RootState) => getPartners(state));
    const isUnsavedChangeAvailable = useSelector(
        (state: RootState) => state.unsavedDataReducer.isUnsavedChangeAvailable
    );
    const isActiveTenantSet = useSelector((state: RootState) =>
        getIsActiveTenantSet(state)
    );
    const [pendingEntity, setPendingEntity] = useState(null);

    let pickerData: { value: any; label: string }[] | null = null;
    let onSelectHandler: (entity: any, route: string) => void;

    const cropName = (name: string) => {
        if (name.length > constants.itemNameLengthLimit) {
            return name.slice(0, constants.itemNameLengthLimit) + "...";
        }

        return name;
    };
    const environmentsPickerList = environments
        ?.map((environment) => ({
            value: environment,
            label: cropName(environment.name),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const tenantsPickerList = isActiveTenantSet
        ? tenants
              .filter((tenant) => !tenant.enrollWorkflowId)
              ?.map((tenant) => ({
                  value: tenant,
                  label: cropName(
                      `${tenant.moniker} - ${tenant.versionInfo.name}`
                  ),
              }))
              .sort((a, b) =>
                  a.value.versionInfo.name.localeCompare(
                      b.value.versionInfo.name
                  )
              )
        : null;

    const customersPickerList = customers
        ?.map((customer) => ({
            value: customer,
            label: cropName(customer.name),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const partnersPickerList = partners
        ?.map((partner) => ({
            value: partner,
            label: cropName(partner.name),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    useEffect(() => {
        if (pendingEntity && itemPath.route) {
            onSelectHandler(pendingEntity, itemPath.route);
            setPendingEntity(null);
        }
    }, [pendingEntity]);

    const handleTenantChange = (tenant: ITenant, route: string) => {
        navigate(
            route
                .replace(":environmentId", environmentId)
                .replace(":tenantId", tenant.versionInfo.id)
        );
    };

    const handleEnvironmentChange = (
        environment: IEnvironment,
        route: string
    ) => {
        navigate(route.replace(":environmentId", environment.id));
    };

    const handleCustomerChange = (customer: ICustomer, route: string) => {
        navigate(route.replace(":customerId", customer.id));
    };

    const handlePartnerChange = (partner: IPartner, route: string) => {
        navigate(route.replace(":partnerId", partner.id));
    };

    if (itemPath.route?.includes(constants.routePath.tenantConfigs)) {
        pickerData = tenantsPickerList;
        onSelectHandler = handleTenantChange;
    } else if (itemPath.route === constants.routePath.tenants) {
        pickerData = environmentsPickerList;
        onSelectHandler = handleEnvironmentChange;
    }

    if (itemPath.route === constants.routePath.customerDetails) {
        pickerData = customersPickerList;
        onSelectHandler = handleCustomerChange;
    }

    if (itemPath.route === constants.routePath.partnerDetails) {
        pickerData = partnersPickerList;
        onSelectHandler = handlePartnerChange;
    }

    const onSelectTabHandler = (entity: any) => {
        if (isUnsavedChangeAvailable) {
            dispatch(
                setModalConfirmCallback({
                    id: uuid(),
                    isAutoDeleteResistant: false,
                    isPageRedirect: true,
                    callback: () => {
                        dispatch(setIsUnsavedChangeAvailable(false));
                        setPendingEntity(entity);
                    },
                })
            );
            dispatch(setRefreshActivated(true));

            return;
        }
        setPendingEntity(entity);
    };

    return (
        <>
            {pickerData ? (
                <SelectPicker
                    data={pickerData}
                    onSelect={onSelectTabHandler}
                    appearance="subtle"
                    className={css.picker}
                    cleanable={false}
                    placeholder={item.name}
                    value={null}
                />
            ) : (
                <NavLink
                    to={item.url}
                    end={true}
                    key={String(index)}
                    className={({ isActive }) =>
                        isActive ? cn(css.selected, css.item) : css.item
                    }
                >
                    {`${item.name}`}
                </NavLink>
            )}
        </>
    );
};

export default BreadCrumbSwitch;
